import { push } from 'connected-react-router';

import { selectionlistConfig } from '../../config';
import { globalErrorResponse, globalWarning, showNotification } from '../notifications/actions';
import * as consts from '../constants';

import * as types from './types';
import {
    CHANGE_ACTIVE_SELECTIONLIST,
    ChangeActiveSelectionlistAction,
    SELECTIONLIST_SEND_FOR_APPROVAL_ERROR,
    SELECTIONLIST_SEND_FOR_APPROVAL_REQUEST,
    SELECTIONLIST_SEND_FOR_APPROVAL_SUCCESS,
    SELECTIONLIST_SUMMARY_SUCCESS,
    SELECTIONLIST_TOGGLE_SELECTIONBAR,
    SELECTIONLISTS_FAILURE,
    SELECTIONLISTS_REQUEST,
    SELECTIONLISTS_SUCCESS,
    SelectionlistSendForApprovalErrorAction,
    SelectionlistSendForApprovalRequestAction,
    SelectionlistSendForApprovalSuccessAction,
    SelectionlistsRequestAction,
    SelectionlistsSuccessAction,
    SelectionlistSummarySuccessAction,
    SelectionlistToggleSelectionbarAction,
    SET_ACTIVE_LIST,
    SET_LIST_SHARED_STATE,
    SetActiveListAction
} from './types';
import { AppDispatch, GetAppState } from '..';
import { SelectionListOut } from '../api';
import i18n from '../../../i18n';
import { http } from '../../api/axios';

const config = selectionlistConfig();

const requestSelectionlists = (): SelectionlistsRequestAction => {
    return { type: SELECTIONLISTS_REQUEST };
};
const receiveSelectionlists = (resp: Array<SelectionListOut>): SelectionlistsSuccessAction => {
    return { type: SELECTIONLISTS_SUCCESS, payload: resp };
};
export const receiveSelectionlistSummary = (resp: SelectionListOut): SelectionlistSummarySuccessAction => {
    return { type: SELECTIONLIST_SUMMARY_SUCCESS, payload: resp };
};

export type OrderListActionUpdateName = { id: string; name: string };
export type OrderListActionVisibility = { listId: string; shared: boolean };

export const approvalStatusError = (error: string): SelectionlistSendForApprovalErrorAction => {
    return { type: SELECTIONLIST_SEND_FOR_APPROVAL_ERROR, payload: error };
};

export function failSelectionlists(error) {
    return (dispatch: AppDispatch, getState: GetAppState) => {
        let existingList = getState().orderlists.list;
        if (!error.runtimeDuplication) {
            switch (error.response.status) {
                case 404:
                    dispatch(globalWarning(error));
                    existingList = [];
                    break;
                default:
                    dispatch(globalErrorResponse(error));
            }
        }

        dispatch({
            type: SELECTIONLISTS_FAILURE,
            payload: existingList,
            error: true
        });
    };
}

export const toggleSelectionBar = (): SelectionlistToggleSelectionbarAction => {
    return { type: SELECTIONLIST_TOGGLE_SELECTIONBAR };
};

type FetchSelectionlistsOptions = {
    /**
     * Force the selection lists to be reloaded, even if a request for refreshing them seems to be in progress.
     * This was added in order to fix PORTAL-3560
     */
    readonly force?: boolean;
};

export function fetchSelectionlists({ force = false }: FetchSelectionlistsOptions = {}) {
    return (dispatch: AppDispatch, getState: GetAppState) => {
        if (getState().orderlists.loading && !force) {
            return;
        }

        dispatch(requestSelectionlists());
        http.get(config.endpoint)
            .then(response => {
                dispatch(receiveSelectionlists(response.data));
            })
            .catch(error => dispatch(failSelectionlists(error)));
    };
}

export const changeActiveSelectionlist = (listId?: string): ChangeActiveSelectionlistAction => {
    return { type: CHANGE_ACTIVE_SELECTIONLIST, payload: listId };
};
export const setActiveList = (resp: SelectionListOut): SetActiveListAction => {
    return { type: SET_ACTIVE_LIST, payload: resp };
};

export const setListSharedState = (shared: boolean) => {
    return { type: SET_LIST_SHARED_STATE, payload: shared };
};

export function createAndGoToNewSelectionlist() {
    const name = i18n.t('notifications.createAndGoToNewSelectionlist.name');
    return (dispatch: AppDispatch) => {
        http.post(config.endpoint, { listName: name })
            .then(response => {
                const newListId = response.data;
                const list = {
                    id: newListId,
                    listName: name,
                    eaudiobooksAmount: 0,
                    eaudiobooksValue: 0,
                    ebooksAmount: 0,
                    ebooksValue: 0,
                    totalNetValue: 0,
                    totalTaxValue: 0,
                    totalValue: 0
                } as SelectionListOut;
                dispatch({
                    type: types.CREATE_SELECTIONLIST,
                    payload: list
                });
                dispatch(push(`${consts.PATH_SELECTIONLISTS}/${newListId}`));
            })
            .catch(error => dispatch(failSelectionlists(error)));
    };
}

export function createSelectionlist(name) {
    return (dispatch: AppDispatch) => {
        const body = JSON.stringify({ listName: name });
        http.post(config.endpoint, body)
            .then(response => {
                const list = {
                    id: response.data,
                    listName: name,
                    eaudiobooksAmount: 0,
                    eaudiobooksValue: 0,
                    ebooksAmount: 0,
                    ebooksValue: 0,
                    totalNetValue: 0,
                    totalTaxValue: 0,
                    totalValue: 0
                } as SelectionListOut;
                dispatch({
                    type: types.CREATE_SELECTIONLIST,
                    payload: list
                });
                dispatch(setActiveList(list));
                dispatch(fetchSelectionlists());
            })
            .catch(error => dispatch(failSelectionlists(error)));
    };
}

export function deleteSelectionlist(listId: string) {
    return (dispatch: AppDispatch) => {
        dispatch({
            type: types.DELETE_SELECTIONLIST,
            payload: listId
        });

        dispatch(push(consts.PATH_CATALOGUE));
        http.delete(config.endpoint, {
            pathParams: {
                listId
            }
        })
            .then(() => dispatch(fetchSelectionlists({ force: true })))
            .catch(error => dispatch(failSelectionlists(error)));
    };
}

export function changeListVisibility(listId: string, shared: boolean) {
    return (dispatch: AppDispatch) => {
        const pathParams = { listId };
        let result;
        if (shared) {
            result = http.put(config.endpointVisibility, {}, { pathParams });
        } else {
            result = http.delete(config.endpointVisibility, { pathParams });
        }
        result
            .then(() => {
                dispatch({
                    type: types.SELECTIONLIST_VISIBILITY,
                    payload: { listId, shared } as OrderListActionVisibility
                });
            })
            .catch(error => dispatch(globalErrorResponse(error)));
    };
}

const sendForApprovalRequest = (): SelectionlistSendForApprovalRequestAction => {
    return { type: SELECTIONLIST_SEND_FOR_APPROVAL_REQUEST };
};
const sendForApprovalSuccess = (): SelectionlistSendForApprovalSuccessAction => {
    return { type: SELECTIONLIST_SEND_FOR_APPROVAL_SUCCESS };
};

export function sendForApproval(id, email) {
    return (dispatch: AppDispatch) => {
        const pathParams = { listId: id };
        const params = { userEmail: email };

        dispatch(sendForApprovalRequest());

        return http
            .get(config.endpointApproval, { params, pathParams })
            .then(() => {
                dispatch(showNotification(i18n.t('notifications.sendForApproval.success')));
                dispatch(sendForApprovalSuccess());
            })
            .catch(error => {
                dispatch(approvalStatusError(error.response.data.message));
                return Promise.reject();
            });
    };
}
